import React from 'react'
import { NextPage } from 'next'
import { getUnixTime } from 'date-fns'
import { siteName } from '~/constants/site'
import { ChainContext } from '~/context/chain'
import { Collection } from '~/domain/Collection'
import { Series } from '~/domain/Series'
import { Vault } from '~/domain/Vault'
import { useAthlete } from '~/hooks/useAthlete'
import { useBenefits } from '~/hooks/useBenefits'
import { useFragment } from '~/hooks/useItem'
import { sendNftViewedEvent } from '~/components/AnalyticsProvider'
import { HtmlPageMetadata } from '~/components/HtmlPageMetadata'
import { NftHeader } from '~/components/NftPage/NftHeader'
import { NftInfo } from '~/components/NftPage/NftInfo'
import { NftDescription } from '~/components/NftPage/NftDescription'
import { NftDetails } from '~/components/NftPage/NftDetails'
import { NftTraits } from '~/components/NftPage/NftTraits'
import { NftActions } from '~/components/NftPage/NftActions'
import { NftMedia } from '~/components/NftPage/NftMedia'
import { NftBenefits } from '~/components/NftPage/NftBenefits'
import { NftAthleteMuseum } from '~/components/NftPage/NftAthleteMuseum'
import { NftActivity } from '~/components/NftPage/NftActivity'
import styles from '~/components/NftPage/index.module.scss'

export interface FragmentPageProps {
  series: Series
  collection: Collection
  tokenId: string
  vault: Vault
  fragmentId: string
  trophyId?: string
  url: string
}

export const FragmentPage: NextPage<FragmentPageProps> = ({
  series,
  collection,
  tokenId,
  vault,
  fragmentId,
  trophyId,
  url,
}) => {
  const { activeChain, setActiveChain } = React.useContext(ChainContext)
  const { data: item, isLoading, isError } = useFragment(collection, tokenId, vault, fragmentId)
  const [owner, setOwner] = React.useState<string | undefined>(item?.nft?.owner)
  const [isListed, setIsListed] = React.useState<boolean>(false)
  const { data: athlete, isLoading: isAthleteLoading } = useAthlete(trophyId)
  const { data: benefits, isLoading: isBenefitsLoading } = useBenefits(trophyId, isListed)
  const now = new Date()

  React.useEffect(() => {
    if (activeChain !== collection.contract.chainId) {
      setActiveChain(collection.contract.chainId)
    }
  }, [activeChain, setActiveChain, collection.contract.chainId])

  React.useEffect(() => {
    if (item?.nft?.metadata) {
      sendNftViewedEvent(collection.contract, item, series)
    }
  }, [collection.contract, item, series])

  if (isLoading) {
    return (
      <div data-testid="fragment-page-loading">
        Loading...
      </div>
    )
  }

  if (isError || !item) {
    return (
      <div data-testid="fragment-page-error">
        Cannot load NFT
      </div>
    )
  }

  if (!owner) {
    setOwner(item.nft.owner)
  }

  return (
    <>
      <HtmlPageMetadata
        title={ `${item.nft.metadata.name} | ${siteName}` }
        description={ `${item.nft.metadata.description}` }
        image={ `${item.nft.metadata.image}` }
        url={ url }
      />

      <div className={ styles['nft-page__container'] } >
        <NftMedia nft={ item }/>

        <NftHeader series={ series }/>

        <NftInfo
          item={ item }
          owner={ owner as string }
        />

        {
          !isAthleteLoading &&
          !isBenefitsLoading &&
          isListed &&
          benefits &&
          benefits.deadline >= getUnixTime(now) &&
            <NftBenefits athlete={ athlete } benefits={ benefits }/>
        }

        {
          !isAthleteLoading &&
          !isBenefitsLoading &&
          (!benefits || benefits.deadline < getUnixTime(now)) &&
          athlete &&
            <NftAthleteMuseum athlete={ athlete }/>
        }

        <NftDescription
          nft={ item.nft }
        />

        <NftActions
          series={ series }
          collection={ vault }
          nft={ item }
          owner={ owner }
          trophyId={ trophyId }
          onOwnerChanged={ setOwner }
          onIsListed={ (value) => setIsListed(value) }
        />

        <NftDetails
          collection={ collection }
          nft={ item.nft }
        />

        {
          owner !== undefined &&
            <NftActivity
              series={ series }
              item={ item }
              trophyId={ trophyId }
            />
        }

        <NftTraits nft={ item.nft }/>
      </div>
    </>
  )
}
